import moment from 'moment';

const CONFIG = {
  capacity: 126,
  stlyOffsetDays: 364,                 // day-of-week aligned STLY
  paceAheadAt: 1.00,                   // pace index >= this = ahead
  adrAheadAt: 1.00,                    // ADR index >= this = ahead
  windows: { priceOnlyAbove: 90, deepAbove: 30, closuresAbove: 7 },  // days before arrival
  hurdleTiers: [ {minFcst:95,pct:.87},{minFcst:90,pct:.82},{minFcst:85,pct:.76},{minFcst:80,pct:.70},{minFcst:70,pct:.62},{minFcst:0,pct:.55} ],
  deepWindowCloseMinFcst: 88,          // 31–90 days: only close if forecast >= this
  healthyTightenMinFcst: 90,           // healthy dates tighten above this forecast
  anyTightenMinFcst: 95,               // any date tightens above this forecast
  barRaise: { min:5, adrGapWeight:.5, fcstPivot:85, fcstWeight:.7 },
  healthyRaise: { minFcst:92, pct:.04, min:4 },
  barLower: { maxPct:.06, adrGapWeight:.5, min:5 },
  maxBarChangePct: .12,
  lowConfidence: { dbaAbove:90, paceBand:.03, minHistoryMonths:6 },
  los: { enabled:true, maxMinLos:3, minNetPctOfHurdle:.85 },            // R9a length-of-stay check
  screener: { topN:14, wPace:30, wAdr:20, wComp:15, wDollar:20, wUrg:15, wEvent:10, paceCap:.30, adrCap:.10, compStart:85, noActionFactor:.5,
              urgency:{closein:1, closures:.8, deep:.5, price:.2} }
};

// Universal Calendar Date parser: extracts YYYY-MM-DD from any date representation
// (ISO string with UTC Z, SQL datetime, slash date, Date object, Moment object)
// without shifting days across timezones.
export function toCalendarYMD(val) {
  if (!val && val !== 0) return '';
  if (typeof val === 'string') {
    const trimmed = val.trim();
    // 1. Matches YYYY-MM-DD anywhere at start (e.g. "2026-10-30", "2026-10-30 00:00:00", "2026-10-30T00:00:00.000Z")
    const match = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      return `${match[1]}-${match[2]}-${match[3]}`;
    }
    // 2. Matches MM/DD/YYYY or M/D/YYYY
    const slashMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (slashMatch) {
      const mm = slashMatch[1].padStart(2, '0');
      const dd = slashMatch[2].padStart(2, '0');
      return `${slashMatch[3]}-${mm}-${dd}`;
    }
    // 3. Matches DD-MM-YYYY or DD.MM.YYYY
    const dmyMatch = trimmed.match(/^(\d{1,2})[-.](\d{1,2})[-.](\d{4})/);
    if (dmyMatch) {
      const dd = dmyMatch[1].padStart(2, '0');
      const mm = dmyMatch[2].padStart(2, '0');
      return `${dmyMatch[3]}-${mm}-${dd}`;
    }
  }
  if (val instanceof Date && !isNaN(val.getTime())) {
    const y = val.getFullYear();
    const m = String(val.getMonth() + 1).padStart(2, '0');
    const d = String(val.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  if (moment.isMoment(val) && val.isValid()) {
    return val.format('YYYY-MM-DD');
  }
  const m = moment(val);
  return m.isValid() ? m.format('YYYY-MM-DD') : '';
}

// Universal timezone-agnostic formatter for calendar dates
export function formatCalendarDate(val, formatString = 'MMM D') {
  const ymd = toCalendarYMD(val);
  if (!ymd) return '—';
  const m = moment(ymd, 'YYYY-MM-DD');
  return m.isValid() ? m.format(formatString) : String(val || '');
}

const AS_OF = toCalendarYMD(moment()) || moment().format('YYYY-MM-DD');
const PROPERTY = '';
const DAILY = [];
const RES17 = [];

// Base benchmark BAR for deriving discounts and fallbacks
const baseBar = 120;

function normalizeChannelName(raw) {
  if (!raw) return 'DIRECT';
  let s = String(raw).trim().toUpperCase();
  s = s.replace(/[\s.-]+/g, '_');
  if (s.endsWith('_COM')) {
    s = s.slice(0, -4);
  } else if (s.endsWith('COM') && s.length > 5 && !['INTERCOM'].includes(s)) {
    s = s.replace(/COM$/, '');
  }
  return s || 'DIRECT';
}

function extractChannelPairs(r) {
  const ch = {};
  if (r?.channelpartnerlist && typeof r.channelpartnerlist === 'object') {
    for (const [partnerName, records] of Object.entries(r.channelpartnerlist)) {
      const canon = normalizeChannelName(partnerName);
      if (!ch[canon]) ch[canon] = [0, 0];
      const list = Array.isArray(records) ? records : [records];
      for (const rec of list) {
        const ty = Number(rec?.cy_roomnight ?? rec?.cy_rooms ?? rec?.ty ?? 0) || 0;
        const ly = Number(rec?.ly_roomnight ?? rec?.ly_rooms ?? rec?.ly ?? 0) || 0;
        ch[canon][0] += ty;
        ch[canon][1] += ly;
      }
    }
  }

  for (const k of Object.keys(r || {})) {
    const m = k.match(/^(.+)_(ty|ly)$/i);
    if (m) {
      const raw = m[1];
      const canon = normalizeChannelName(raw);
      if (!ch[canon]) {
        ch[canon] = [0, 0];
        const val = Number(r[k]) || 0;
        if (m[2].toLowerCase() === 'ty') {
          ch[canon][0] = val;
        } else {
          ch[canon][1] = val;
        }
      } else if (!r?.channelpartnerlist || Object.keys(r.channelpartnerlist).length === 0) {
        const val = Number(r[k]) || 0;
        if (m[2].toLowerCase() === 'ty') {
          ch[canon][0] = val;
        } else {
          ch[canon][1] = val;
        }
      }
    }
  }
  // Backward compatibility aliases if any code references mixed-case names
  if (ch['BRAND'] && !ch['Brand.com']) ch['Brand.com'] = ch['BRAND'];
  if (ch['BOOKING'] && !ch['Booking.com']) ch['Booking.com'] = ch['BOOKING'];
  if (ch['EXPEDIA'] && !ch['Expedia']) ch['Expedia'] = ch['EXPEDIA'];
  if (ch['Brand.com'] && !ch['BRAND']) ch['BRAND'] = ch['Brand.com'];
  if (ch['Booking.com'] && !ch['BOOKING']) ch['BOOKING'] = ch['Booking.com'];
  if (ch['Expedia'] && !ch['EXPEDIA']) ch['EXPEDIA'] = ch['Expedia'];
  return ch;
}

function buildDatesFromDaily(dailyList, activeAsOfDate, capacity = CONFIG.capacity) {
  const list = Array.isArray(dailyList) ? dailyList : [];
  if (!list.length) return [];
  const asOf = toCalendarYMD(activeAsOfDate) || AS_OF;
  const C = Number(capacity) || CONFIG.capacity || 126;
  const barValues = list.map(d => Number(d.bar)).filter(b => b > 0);
  const avgBar = barValues.length ? Math.round(barValues.reduce((a, b) => a + b, 0) / barValues.length) : baseBar;

  return list.map(r => {
    const otbRooms = Number(r.otb_rooms ?? r.rooms ?? 0);
    const otbRev = Number(r.otb_rev ?? r.rev ?? 0);
    const stlyRooms = Number(r.stly_otb_rooms ?? r.stly_rooms ?? 0);
    const stlyRev = Number(r.stly_otb_rev ?? r.stly_rev ?? 0);
    const fcRooms = Number(r.forecast_rooms ?? r.fc_rooms ?? otbRooms);

    const adr = otbRooms > 0 ? otbRev / otbRooms : 0;
    const stlyAdr = stlyRooms > 0 ? stlyRev / stlyRooms : 0;
    const bar = Number(r.bar) > 0 ? Number(r.bar) : Math.round(adr || avgBar || 120);

    const rawStayDate = r.stay_date || r.date || r.d;
    const stayDate = toCalendarYMD(rawStayDate);
    const mStay = moment(stayDate, 'YYYY-MM-DD');
    const mAsOf = moment(asOf, 'YYYY-MM-DD');
    const dba = (mStay.isValid() && mAsOf.isValid()) ? mStay.diff(mAsOf, 'days') : 0;
    const ch = extractChannelPairs(r);

    return {
      d: stayDate,
      ev: r.event || r.ev || '',
      dba: isNaN(dba) ? 0 : dba,
      otb: C > 0 ? (otbRooms / C) * 100 : 0,
      stly: C > 0 ? (stlyRooms / C) * 100 : 0,
      fcst: C > 0 ? (Math.max(fcRooms, otbRooms) / C) * 100 : 0,
      fcR: fcRooms,
      adr,
      stlyAdr,
      bar,
      ch,
      channelpartnerlist: r.channelpartnerlist || {}
    };
  });
}

const DATES = [];

const DEFAULT_FALLBACK_PLANS = [
  { code: 'BAR', name: 'Best Available Rate', channel: 'DIRECT', type: 'BAR', commission_pct: 0, is_lead_bar: true, minDba: 0 },
  { code: 'BAR_OTA', name: 'OTA Standard Rate', channel: 'BOOKING', type: 'BAR', commission_pct: 18, is_lead_bar: false, minDba: 0 },
  { code: 'ADV14', name: 'Advance Purchase 14D', channel: 'DIRECT', type: 'Discount', commission_pct: 0, is_lead_bar: false, minDba: 14, disc: 0.15 },
  { code: 'AAA', name: 'AAA / Auto Club', channel: 'DIRECT', type: 'Discount', commission_pct: 0, is_lead_bar: false, minDba: 0, disc: 0.10 },
  { code: 'GOV', name: 'Government Rate', channel: 'DIRECT', type: 'Negotiated', commission_pct: 0, is_lead_bar: false, minDba: 0, fixed: 107, protected: true }
];

const DEFAULT_PLANS_META = {};

function enrichRatePlans(plans = [], reservations = []) {
  const resSource = Array.isArray(reservations) && reservations.length ? reservations : [];
  // If no rate plans are provided, auto-discover distinct rate plans from reservations
  if (!plans || plans.length === 0) {
    const distinct = new Map();
    let leadBarAssigned = false;
    for (const r of resSource) {
      const code = r.rate_plan_code || r.plan;
      if (!code || distinct.has(code)) continue;
      const ch = normalizeChannelName(r.channel || 'DIRECT');
      const isBar = code.toUpperCase().includes('BAR') || code.toUpperCase().includes('RCK') || code.toUpperCase().includes('RACK');
      const isGroup = code.toUpperCase().startsWith('G-') || code.toUpperCase().startsWith('GRP');
      const isAdv = code.toUpperCase().includes('ADP') || code.toUpperCase().includes('ADV');
      const isFixed = r.is_fixed === 'Y' || r.is_fixed === true;
      const type = isBar ? 'BAR' : isGroup ? 'Group' : isFixed ? 'Negotiated' : 'Discount';
      const isOTA = ch.toUpperCase().includes('EXPEDIA') || ch.toUpperCase().includes('BOOKING');

      // Ratecode-wise commission from reservations
      const planRes = resSource.filter(x => (x.rate_plan_code || x.plan) === code);
      const withComm = planRes.filter(x => x.commission !== undefined && x.commission !== null);
      let commPct = 0.0;
      if (withComm.length > 0) {
        const avgComm = withComm.reduce((sum, x) => sum + (Number(x.commission) || 0), 0) / withComm.length;
        commPct = avgComm <= 1 && avgComm > 0 ? avgComm * 100 : avgComm;
      } else if (isOTA) {
        commPct = 18.0;
      }

      distinct.set(code, {
        code,
        name: r.rate_plan_name || code,
        channel: ch,
        type,
        commission_pct: commPct,
        is_lead_bar: isBar && !leadBarAssigned,
        minDba: isAdv ? 14 : 0
      });
      if (isBar) leadBarAssigned = true;
    }
    plans = Array.from(distinct.values());
  }

  if (!plans || plans.length === 0) {
    plans = DEFAULT_FALLBACK_PLANS;
  }

  const activeRes = resSource.filter(r => r.status === 'Confirmed' || r.status === 'In-house');
  const totalActive = activeRes.length;
  let hasLeadBar = false;

  const enrichedPlans = plans.map(p => {
    const key = `${p.code}|${p.channel || ''}`;
    const baseMeta = DEFAULT_PLANS_META[key] || DEFAULT_PLANS_META[p.code];
    const enriched = { ...p };

    // 1. Normalize commission: supports both `commission_pct` (e.g. 0.00, 15.00) and `comm` (e.g. 0.15)
    const rawComm = enriched.comm ?? enriched.commission_pct ?? baseMeta?.comm ?? 0;
    enriched.comm = rawComm > 1 ? rawComm / 100 : rawComm;

    // 2. Normalize lead BAR flag: supports both `is_lead_bar` and `isLeadBar`
    if (enriched.isLeadBar === undefined) {
      enriched.isLeadBar = enriched.is_lead_bar ?? baseMeta?.isLeadBar ?? (
        enriched.type === 'BAR' && (
          enriched.code === 'BAR' ||
          enriched.code === 'RACK' ||
          enriched.channel?.toUpperCase() === 'BRAND' ||
          enriched.channel?.toUpperCase() === 'BRAND.COM' ||
          enriched.channel?.toUpperCase() === 'DIRECT' ||
          enriched.comm === 0
        )
      );
    }
    if (enriched.isLeadBar) hasLeadBar = true;

    // 3. Normalize protected status: Group and Negotiated rates are contractual and never closed
    if (enriched.protected === undefined) {
      enriched.protected = baseMeta?.protected ?? (
        enriched.type === 'Negotiated' ||
        enriched.type === 'Group' ||
        enriched.type === 'Contract' ||
        enriched.code?.startsWith('G-')
      );
    }

    // 4. Derive rate math (disc or fixed rate) if not provided
    if (enriched.disc === undefined && enriched.fixed === undefined) {
      if (baseMeta?.disc != null) {
        enriched.disc = baseMeta.disc;
      } else if (baseMeta?.fixed != null) {
        enriched.fixed = baseMeta.fixed;
      } else if (enriched.type === 'BAR') {
        enriched.disc = 0;
      } else {
        const planRes = resSource.filter(r => (r.rate_plan_code || r.plan) === enriched.code);
        if (planRes.length > 0) {
          const avgRate = planRes.reduce((s, r) => s + (Number(r.nightly_rate ?? r.sell) || 0), 0) / planRes.length;
          const avgNet = planRes.reduce((s, r) => s + (Number(r.net_rate ?? r.nightly_rate ?? r.sell) || 0), 0) / planRes.length;
          enriched.avgRate = Math.round(avgRate);
          enriched.avgNet = Math.round(avgNet);
          if (enriched.protected || planRes.some(r => r.is_fixed === 'Y')) {
            enriched.fixed = Math.round(avgRate);
          } else if (avgRate >= baseBar && enriched.type !== 'BAR') {
            enriched.fixed = Math.round(avgRate);
          } else if (baseBar > 0 && avgRate < baseBar) {
            enriched.disc = Math.max(0.01, Math.min(0.70, Math.round((1 - avgRate / baseBar) * 100) / 100));
          } else {
            enriched.disc = enriched.type === 'Discount' ? 0.10 : 0.05;
          }
        } else {
          enriched.disc = enriched.type === 'Discount' ? 0.10
            : enriched.type === 'Opaque' ? 0.25
            : enriched.type === 'Negotiated' ? 0.15
            : enriched.type === 'Group' ? 0.20
            : 0.10;
        }
      }
    }

    // 5. Automatically derive volume share from reservations or baseline if omitted
    if (enriched.share === undefined) {
      if (baseMeta?.share != null) {
        enriched.share = baseMeta.share;
      } else {
        const planRes = activeRes.filter(r => (r.rate_plan_code || r.plan) === enriched.code);
        enriched.share = totalActive > 0 ? Math.round((planRes.length / totalActive) * 100) / 100 : Math.round((1 / plans.length) * 100) / 100;
        if (!enriched.share || enriched.share < 0.01) enriched.share = 0.02;
      }
    }

    // 6. Automatically derive lead time distribution from bookings or baseline if omitted
    if (!enriched.lead || !enriched.lead.length) {
      if (baseMeta?.lead != null) {
        enriched.lead = baseMeta.lead;
      } else {
        const planRes = resSource.filter(r => (r.rate_plan_code || r.plan) === enriched.code);
        const buckets = [0, 0, 0, 0, 0, 0]; // 0-3, 4-7, 8-14, 15-30, 31-60, 61+
        for (const r of planRes) {
          const arr = r.arrival_date || r.arrival;
          const bkd = r.booking_date || r.booked;
          if (arr && bkd) {
            const leadDays = Math.round((Date.parse(arr) - Date.parse(bkd)) / 864e5);
            if (leadDays <= 3) buckets[0]++;
            else if (leadDays <= 7) buckets[1]++;
            else if (leadDays <= 14) buckets[2]++;
            else if (leadDays <= 30) buckets[3]++;
            else if (leadDays <= 60) buckets[4]++;
            else buckets[5]++;
          }
        }
        const count = buckets.reduce((a, b) => a + b, 0);
        if (count >= 3) {
          enriched.lead = buckets.map(c => Math.round((c / count) * 100));
        } else {
          enriched.lead = enriched.type === 'Group' ? [0, 0, 0, 6, 30, 64]
            : enriched.minDba ? [0, 0, 4, 40, 34, 22]
            : enriched.channel?.toUpperCase().includes('BOOKING') && enriched.disc ? [38, 24, 18, 12, 6, 2]
            : [14, 14, 18, 24, 18, 12];
        }
      }
    }

    return enriched;
  });

  if (!hasLeadBar && enrichedPlans.length > 0) {
    const firstBar = enrichedPlans.find(p => p.type === 'BAR') || enrichedPlans[0];
    if (firstBar) firstBar.isLeadBar = true;
  }

  return enrichedPlans;
}

const PLANS = enrichRatePlans([], []);

const LEAD_BUCKETS = ['0–3', '4–7', '8–14', '15–30', '31–60', '61+'];

const Q = {
  under: { id: 'UNDERPRICED', label: 'Underpriced', color: '--under', sub: "Pace is ahead of last year but ADR is behind. You're leaving money on the table." },
  healthy: { id: 'HEALTHY', label: 'Healthy', color: '--healthy', sub: 'Pace and ADR are both ahead of last year. Hold rates and protect close-in inventory.' },
  resist: { id: 'PRICE_RESISTANCE', label: 'Price resistance', color: '--resist', sub: 'ADR is ahead of last year but pace is behind. Check the compset before cutting.' },
  demand: { id: 'DEMAND_GAP', label: 'Demand gap', color: '--demand', sub: "Pace and ADR are both behind. This is a demand problem, not a price problem. Don't chase rate down." }
};

const LEVERS = {
  price: { id: 'PRICE_ONLY', text: '<b>90+ days out.</b> Price only. Too early to close plans.' },
  deep: { id: 'PRICE_PLUS_DEEP_CLOSE', text: '<b>31–90 days out.</b> Price, plus closing the deepest discount plans if the forecast shows compression.' },
  closures: { id: 'CLOSURES', text: '<b>8–30 days out.</b> Plan closures, channel-specific stop-sells and MinLOS are in play.' },
  closein: { id: 'CLOSE_IN', text: '<b>0–7 days out.</b> Close-to-arrival, last-minute rate moves, and reopening if pickup stalls.' }
};

const RULES = [
  ['R1', 'PROTECTED', 'Plan is negotiated or group', 'Protected, never closed'],
  ['R2', 'WINDOW_PASSED', 'Plan has min booking window and days out < window', 'No action, plan not sellable'],
  ['R3', 'BAR_MOVE', 'Lead BAR row and BAR move ≠ 0', 'Raise or lower BAR on all channels'],
  ['R4', 'BAR_DERIVED', 'OTA BAR row', 'Follows lead BAR, no separate action'],
  ['R5', 'BAR_HOLD', 'BAR row and BAR move = 0', 'Hold'],
  ['R6', 'REOPEN', 'Plan closed, date is resistance or demand gap, not price-only window, net ≥ hurdle', 'Reopen'],
  ['R7', 'STAY_CLOSED', 'Plan closed and R6 not met', 'Stay closed'],
  ['R8', 'PRICE_ONLY', 'Days out > 90', 'Keep open'],
  ['R9', 'CLOSE_BELOW_HURDLE', 'Closure allowed for window and net < hurdle', 'Close (close-to-arrival inside 7 days)'],
  ['R9a', 'MINLOS_INSTEAD_OF_CLOSE', 'R9 met, net ≥ 85% of hurdle, and a 2–3 night stay containing the night clears the summed hurdles', 'Set MinLOS n'],
  ['R10', 'KEEP_OPEN', 'None of the above', 'Keep open']
];

const RES_FIELDS = [
  ['confirmation_no', 'PMS confirmation number'], ['guest_name', 'Masked in UI (last name + initial)'],
  ['booking_date / booking_ts', 'Needed to rebuild OTB at any days-before-arrival'], ['cancel_date / cancel_ts', 'Needed so STLY OTB excludes rooms cancelled later'],
  ['arrival_date, departure_date, nights', 'Stay span; reservation counts on every night it covers'], ['room_type_code', 'Room category'],
  ['rate_plan_code, channel, source_code', 'Must map to the rate plan catalogue'], ['market_segment', 'From rate code classification'],
  ['nightly_rate (per stay date)', 'Rate for the specific night, not the stay average'], ['commission_pct, package_cost', 'For net rate'], ['status', 'Confirmed, Cancelled, No-show, In-house']
];

const LB = [[0, 3], [4, 7], [8, 14], [15, 30], [31, 60], [61, 120]];
const ROOMS = [['KNG', 'King', .45, 0], ['DQQ', 'Double Queen', .42, 0], ['KSTE', 'King Suite', .13, 40]];
const LASTN = ['Patel', 'Johnson', 'Garcia', 'Nguyen', 'Smith', 'Williams', 'Brown', 'Davis', 'Martinez', 'Lee', 'Walker', 'Hall', 'Allen', 'Young', 'King', 'Wright', 'Lopez', 'Hill', 'Scott', 'Green', 'Adams', 'Baker', 'Nelson', 'Carter', 'Mitchell', 'Perez', 'Roberts', 'Turner', 'Phillips', 'Campbell', 'Parker', 'Evans', 'Edwards', 'Collins', 'Stewart', 'Sanchez', 'Morris', 'Rogers', 'Reed', 'Cook', 'Shah', 'Kim', 'Chen', 'Singh', 'Murphy', 'Rivera', 'Desai', 'Thomas'];

const RES_COLS = [
  ['conf', 'Confirmation'], ['guest', 'Guest'], ['booked', 'Booked'], ['lead', 'Lead (days)', 'r'], ['arrival', 'Arrival'], ['nights', 'Nights', 'r'], ['room', 'Room type'],
  ['plan', 'Rate plan'], ['channel', 'Channel'], ['segment', 'Segment'], ['rate', 'Rate', 'r'], ['net', 'Net', 'r'], ['vsToday', 'vs. today', 'r'], ['status', 'Status']
];

// Collect all distinct property channels across all DAILY entries
const propertyChannelSet = new Set();
for (const d of DATES) {
  if (d.ch) {
    for (const chName of Object.keys(d.ch)) {
      if (chName && !['Brand.com', 'Booking.com', 'Expedia'].includes(chName)) {
        propertyChannelSet.add(chName);
      }
    }
  }
}
const CHANNELS = Array.from(propertyChannelSet).sort((a, b) => {
  if (a === 'BRAND') return -1;
  if (b === 'BRAND') return 1;
  return a.localeCompare(b);
});
if (!CHANNELS.length) {
  CHANNELS.push('BRAND', 'BOOKING', 'EXPEDIA');
}

const baseOwners = {
  'BRAND':       { cls: 'o-brand', comm: 0 },
  'DIRECT':      { cls: 'o-brand', comm: 0 },
  'BOOKING':     { cls: 'o-booking', comm: 0.18 },
  'EXPEDIA':     { cls: 'o-expedia', comm: 0.18 },
  'AGODA':       { cls: 'o-agoda', comm: 0.18 },
  'OYO':         { cls: 'o-oyo', comm: 0.18 },
  'AIRBNB':      { cls: 'o-airbnb', comm: 0.15 },
  'GDS':         { cls: 'o-brand', comm: 0.10 },
  'UNMAPPED':    { cls: 'o-unmapped', comm: 0.18 },
  // Legacy aliases
  'Brand.com':   { cls: 'o-brand', comm: 0 },
  'Booking.com': { cls: 'o-booking', comm: 0.18 },
  'Expedia':     { cls: 'o-expedia', comm: 0.18 },
  'Agoda':       { cls: 'o-agoda', comm: 0.18 },
  'Direct':      { cls: 'o-brand', comm: 0 }
};

const PROMO_OWNERS = new Proxy(baseOwners, {
  get(target, prop) {
    if (typeof prop !== 'string' || prop === 'then' || prop === 'toJSON') return target[prop];
    if (prop in target) return target[prop];
    const upper = prop.toUpperCase();
    if (upper in target) return target[upper];
    const isBrandLike = /^(brand|direct)/i.test(prop);
    const slug = prop.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'channel';
    const entry = { cls: `o-${slug}`, comm: isBrandLike ? 0 : 0.18 };
    target[prop] = entry;
    return entry;
  }
});

const basePromoNames = {
  'BRAND':       { LAST_MINUTE:'Last-minute offer to email and app audience', MEMBER:'Loyalty member rate', EARLY_BOOKER:'Advance purchase offer', VALUE_ADD:'Stay longer and save, breakfast included', EVENT:'Event package' },
  'BOOKING':     { LAST_MINUTE:'Last-minute deal', MEMBER:'Genius member discount', EARLY_BOOKER:'Early booker deal', VALUE_ADD:'Visibility boost with breakfast-included rate', EVENT:'Event package' },
  'EXPEDIA':     { LAST_MINUTE:'Same-day and last-minute deal', MEMBER:'Member Only Deal', EARLY_BOOKER:'Early booking discount', VALUE_ADD:'Visibility boost with package rate', EVENT:'Event package' },
  'AGODA':       { LAST_MINUTE:'Agoda Last-minute deal', MEMBER:'Agoda VIP member deal', EARLY_BOOKER:'Agoda Early bird discount', VALUE_ADD:'Agoda Growth Express visibility boost', EVENT:'Event package' },
  'OYO':         { LAST_MINUTE:'OYO Flash sale deal', MEMBER:'OYO Wizard member offer', EARLY_BOOKER:'OYO Advance booking deal', VALUE_ADD:'OYO Preferred visibility boost', EVENT:'OYO Event package' },
  'UNMAPPED':    { LAST_MINUTE:'Last-minute channel offer', MEMBER:'Channel member deal', EARLY_BOOKER:'Early booker deal', VALUE_ADD:'Channel visibility boost', EVENT:'Event package' },
  // Legacy aliases
  'Brand.com':   { LAST_MINUTE:'Last-minute offer to email and app audience', MEMBER:'Loyalty member rate', EARLY_BOOKER:'Advance purchase offer', VALUE_ADD:'Stay longer and save, breakfast included', EVENT:'Event package' },
  'Booking.com': { LAST_MINUTE:'Last-minute deal', MEMBER:'Genius member discount', EARLY_BOOKER:'Early booker deal', VALUE_ADD:'Visibility boost with breakfast-included rate', EVENT:'Event package' },
  'Expedia':     { LAST_MINUTE:'Same-day and last-minute deal', MEMBER:'Member Only Deal', EARLY_BOOKER:'Early booking discount', VALUE_ADD:'Visibility boost with package rate', EVENT:'Event package' },
  'Agoda':       { LAST_MINUTE:'Agoda Last-minute deal', MEMBER:'Agoda VIP member deal', EARLY_BOOKER:'Agoda Early bird discount', VALUE_ADD:'Agoda Growth Express visibility boost', EVENT:'Event package' }
};

const PROMO_NAMES = new Proxy(basePromoNames, {
  get(target, prop) {
    if (typeof prop !== 'string' || prop === 'then' || prop === 'toJSON') return target[prop];
    if (prop in target) return target[prop];
    const upper = prop.toUpperCase();
    if (upper in target) return target[upper];
    const channel = prop;
    const entry = {
      LAST_MINUTE: `${channel} Last-minute deal`,
      MEMBER: `${channel} Member discount`,
      EARLY_BOOKER: `${channel} Early booking deal`,
      VALUE_ADD: `${channel} Visibility boost with value-add package`,
      EVENT: `${channel} Event package`
    };
    target[prop] = entry;
    return entry;
  }
});

const PROMO_FENCES = {
  LAST_MINUTE: 'Book within 7 days of arrival',
  MEMBER: 'Signed-in members only, not shown publicly',
  EARLY_BOOKER: 'Book 30+ days ahead, non-refundable',
  VALUE_ADD: 'Minimum 2-night stay or package inclusion, no public rate cut',
  EVENT: 'Package with event-related inclusion, 2-night minimum'
};

const PROMO_TYPE_LABEL = {
  LAST_MINUTE: 'Last-minute',
  MEMBER: 'Member-only',
  EARLY_BOOKER: 'Early booker',
  VALUE_ADD: 'Value-add',
  EVENT: 'Event package'
};

const PROMO_RULES = [
  ['P1', 'BLACKOUT', 'Underpriced, or healthy with forecast ≥ 90%, or any forecast ≥ 95%', 'Exclude date from every promotion; pause member and mobile discounts'],
  ['P2', 'NO_PROMO', 'Healthy with forecast < 90%', 'Keep current promotions, add nothing'],
  ['P3', 'OWNER', 'Channel with the largest room shortfall vs. STLY', 'That channel owns the campaign; BRAND if no channel is behind'],
  ['P4', 'VALUE_ADD', 'Price resistance (ADR ahead, pace behind)', 'Package or LOS offer plus visibility boost; no rate cut'],
  ['P5', 'TYPE_BY_WINDOW', 'Demand gap: ≤ 7 days / 8–45 days / > 45 days', 'Last-minute / member-only / early booker'],
  ['P6', 'SIZE', 'Desired 10% (15% if pace index < 0.85)', 'Capped so promo net rate ≥ hurdle; cap < 5% switches to value-add'],
  ['P7', 'EVENT', 'Demand gap on an event date', 'Adds a BRAND event package'],
  ['P8', 'GROUP', 'Same owner and type across dates', 'Merged into one campaign; blackout dates in its span are listed']
];

function bindDailyFromRecommendedRates(apiRates, baseDaily = []) {
  if (!Array.isArray(apiRates) || apiRates.length === 0) {
    return Array.isArray(baseDaily) ? baseDaily : [];
  }

  const existingList = Array.isArray(baseDaily) ? [...baseDaily] : [];
  const existingMap = new Map();
  existingList.forEach((item, index) => {
    if (item?.stay_date) {
      existingMap.set(item.stay_date, index);
    }
  });

  const updated = [...existingList];

  apiRates.forEach(item => {
    const rawDate = item.staydate || item.stay_date || item.date;
    if (!rawDate) return;
    const dateStr = toCalendarYMD(rawDate);
    if (!dateStr) return;

    const otb = Number(item.otb) || 0;
    const adr = Number(item.adr) || 0;
    const stly_otb = Number(item.stly_otb) || 0;
    const stly_adr = Number(item.stly_adr) || 0;
    const forecast_rms = Number(item.forecast_rms) || 0;
    const otb_rev = Number((otb * adr).toFixed(2));
    const stly_otb_rev = Number((stly_otb * stly_adr).toFixed(2));

    const rawRate =
      item.bar ??
      item.ota_finalrate ??
      item.recommended_rate ??
      item.forecast_rate ??
      item.rate ??
      item.Rate ??
      item.final_rate ??
      item.finalrate;
    const cleanRate = String(rawRate || "").replace(/[^0-9.]/g, "");
    const parsedBar = Number(cleanRate) || (adr > 0 ? Math.round(adr) : 0);

    if (existingMap.has(dateStr)) {
      const idx = existingMap.get(dateStr);
      const existingBar = updated[idx].bar || 0;
      const finalBar = existingBar > 0 ? existingBar : (parsedBar > 0 ? parsedBar : 0);
      updated[idx] = {
        ...updated[idx],
        stay_date: dateStr,
        otb_rooms: otb,
        otb_rev: otb_rev,
        stly_otb_rooms: stly_otb,
        stly_otb_rev: stly_otb_rev,
        forecast_rooms: forecast_rms,
        bar: finalBar,
        channelpartnerlist: updated[idx].channelpartnerlist || item.channelpartnerlist || {},
      };
    } else {
      const newEntry = {
        stay_date: dateStr,
        event: item.event || "",
        otb_rooms: otb,
        otb_rev: otb_rev,
        stly_otb_rooms: stly_otb,
        stly_otb_rev: stly_otb_rev,
        forecast_rooms: forecast_rms,
        bar: parsedBar,
        brand_ty: Number(item.brand_ty) || 0,
        brand_ly: Number(item.brand_ly) || 0,
        booking_ty: Number(item.booking_ty) || 0,
        booking_ly: Number(item.booking_ly) || 0,
        expedia_ty: Number(item.expedia_ty) || 0,
        expedia_ly: Number(item.expedia_ly) || 0,
        channelpartnerlist: item.channelpartnerlist || {},
      };
      existingMap.set(dateStr, updated.length);
      updated.push(newEntry);
    }
  });

  updated.sort((a, b) => (a.stay_date > b.stay_date ? 1 : -1));
  return updated;
}

function bindDailyFromRateShop(rateShopList, baseDaily = []) {
  if (!Array.isArray(rateShopList) || rateShopList.length === 0) {
    return Array.isArray(baseDaily) ? baseDaily : [];
  }

  const existingList = Array.isArray(baseDaily) ? [...baseDaily] : [];
  const existingMap = new Map();
  existingList.forEach((item, index) => {
    if (item?.stay_date) {
      existingMap.set(item.stay_date, index);
    }
  });

  const updated = [...existingList];

  rateShopList.forEach(item => {
    const isSelf =
      item.IsSelf === true ||
      item.isSelf === true ||
      item.is_self === true ||
      item.IsSelf === 1 ||
      item.isSelf === 1 ||
      String(item.IsSelf || item.isSelf || item.is_self).toLowerCase() === "true";
    if (!isSelf) return;

    const rawDate =
      item.CheckInDate ||
      item.checkInDate ||
      item.checkindate ||
      item.staydate ||
      item.stay_date;
    if (!rawDate) return;
    const dateStr = toCalendarYMD(rawDate);
    if (!dateStr) return;

    const rawRate = item.Rate != null ? item.Rate : (item.rate != null ? item.rate : item.bar);
    const cleanRate = String(rawRate || "").replace(/[^0-9.]/g, "");
    const bar = Number(cleanRate) || 0;

    if (existingMap.has(dateStr)) {
      const idx = existingMap.get(dateStr);
      const finalBar = bar > 0 ? bar : (updated[idx].bar || 0);
      updated[idx] = {
        ...updated[idx],
        stay_date: dateStr,
        bar: finalBar,
      };
    } else {
      const newEntry = {
        stay_date: dateStr,
        event: "",
        otb_rooms: 0,
        otb_rev: 0,
        stly_otb_rooms: 0,
        stly_otb_rev: 0,
        forecast_rooms: 0,
        bar: bar,
        brand_ty: 0,
        brand_ly: 0,
        booking_ty: 0,
        booking_ly: 0,
        expedia_ty: 0,
        expedia_ly: 0,
      };
      existingMap.set(dateStr, updated.length);
      updated.push(newEntry);
    }
  });

  updated.sort((a, b) => (a.stay_date > b.stay_date ? 1 : -1));
  return updated;
}

function bindDailyFromEvents(eventList, baseDaily = []) {
  if (!Array.isArray(eventList) || eventList.length === 0) {
    return Array.isArray(baseDaily) ? baseDaily : [];
  }

  const existingList = Array.isArray(baseDaily) ? [...baseDaily] : [];
  const existingMap = new Map();
  existingList.forEach((item, index) => {
    if (item?.stay_date) {
      existingMap.set(item.stay_date, index);
    }
  });

  const updated = [...existingList];

  eventList.forEach(item => {
    if (!item) return;
    if (item.isactive === false || item.isActive === false) return;

    const eventName = (
      item.eventname ||
      item.eventName ||
      item.event_name ||
      item.event ||
      item.name ||
      ""
    ).toString().trim();
    if (!eventName) return;

    const rawStart =
      item.startdate ||
      item.startDate ||
      item.start_date ||
      item.stay_date ||
      item.staydate;
    if (!rawStart) return;
    const startStr = toCalendarYMD(rawStart);
    if (!startStr) return;

    const rawEnd =
      item.enddate ||
      item.endDate ||
      item.end_date ||
      rawStart;
    const endStr = toCalendarYMD(rawEnd) || startStr;

    const mStart = moment(startStr, "YYYY-MM-DD");
    const mEnd = moment(endStr, "YYYY-MM-DD");
    const diffDays =
      mStart.isValid() && mEnd.isValid() && mEnd.isSameOrAfter(mStart)
        ? Math.min(mEnd.diff(mStart, "days"), 60)
        : 0;

    for (let i = 0; i <= diffDays; i++) {
      const curDateStr = mStart.clone().add(i, "days").format("YYYY-MM-DD");

      if (existingMap.has(curDateStr)) {
        const idx = existingMap.get(curDateStr);
        const existingEvent = updated[idx].event ? String(updated[idx].event).trim() : "";
        let finalEvent = eventName;
        if (existingEvent) {
          const events = existingEvent.split(", ").map(s => s.trim());
          if (!events.includes(eventName)) {
            finalEvent = `${existingEvent}, ${eventName}`;
          } else {
            finalEvent = existingEvent;
          }
        }
        updated[idx] = {
          ...updated[idx],
          stay_date: curDateStr,
          event: finalEvent,
        };
      } else {
        const newEntry = {
          stay_date: curDateStr,
          event: eventName,
          otb_rooms: 0,
          otb_rev: 0,
          stly_otb_rooms: 0,
          stly_otb_rev: 0,
          forecast_rooms: 0,
          bar: 0,
          brand_ty: 0,
          brand_ly: 0,
          booking_ty: 0,
          booking_ly: 0,
          expedia_ty: 0,
          expedia_ly: 0,
        };
        existingMap.set(curDateStr, updated.length);
        updated.push(newEntry);
      }
    }
  });

  updated.sort((a, b) => (a.stay_date > b.stay_date ? 1 : -1));
  return updated;
}

function bindDailyFromChannelStly(channelStlyList, baseDaily = []) {
  if (!Array.isArray(channelStlyList) || channelStlyList.length === 0) {
    return Array.isArray(baseDaily) ? baseDaily : [];
  }

  const existingList = Array.isArray(baseDaily) ? [...baseDaily] : [];
  const existingMap = new Map();
  existingList.forEach((item, index) => {
    if (item?.stay_date) {
      existingMap.set(item.stay_date, index);
    }
  });

  const updated = [...existingList];
  const initializedDates = new Set();

  channelStlyList.forEach((item) => {
    const rawDate =
      item.staydate ||
      item.stayDate ||
      item.stay_date ||
      item.StayDate ||
      item.date;
    if (!rawDate) return;
    const dateStr = toCalendarYMD(rawDate);
    if (!dateStr) return;

    const channelPartner = String(
      item.channelpartner ||
      item.channelPartner ||
      item.ChannelPartner ||
      item.channel ||
      item.Channel ||
      ""
    ).trim();
    if (!channelPartner) return;

    const cyRooms =
      Number(
        item.cy_roomnight ??
        item.cy_roomnights ??
        item.cy_rooms ??
        item.cyRoomnight ??
        item.CY_ROOMNIGHT ??
        item.ty ??
        0
      ) || 0;

    const lyRooms =
      Number(
        item.ly_roomnight ??
        item.ly_roomnights ??
        item.ly_rooms ??
        item.lyRoomnight ??
        item.LY_ROOMNIGHT ??
        item.ly ??
        0
      ) || 0;

    // Normalizing channel partner key (e.g., "BOOKING.COM" -> "booking", "BRAND" -> "brand", "EXPEDIA" -> "expedia")
    const channelKey = channelPartner
      .toLowerCase()
      .replace(/[\s.-]+/g, "_")
      .replace(/_com$/, "");

    let targetIdx;
    if (existingMap.has(dateStr)) {
      targetIdx = existingMap.get(dateStr);
    } else {
      const newEntry = {
        stay_date: dateStr,
        event: "",
        otb_rooms: 0,
        otb_rev: 0,
        stly_otb_rooms: 0,
        stly_otb_rev: 0,
        forecast_rooms: 0,
        bar: 0,
        brand_ty: 0,
        brand_ly: 0,
        booking_ty: 0,
        booking_ly: 0,
        expedia_ty: 0,
        expedia_ly: 0,
        channelpartnerlist: {},
      };
      targetIdx = updated.length;
      existingMap.set(dateStr, targetIdx);
      updated.push(newEntry);
    }

    const currentEntry = { ...updated[targetIdx] };

    if (!initializedDates.has(dateStr)) {
      initializedDates.add(dateStr);
      currentEntry.channelpartnerlist = {};
      // Reset channel keys for this date so they reflect the fetched data cleanly
      Object.keys(currentEntry).forEach((k) => {
        if (/_(ty|ly)$/i.test(k)) {
          currentEntry[k] = 0;
        }
      });
    }

    const currentChannelList = { ...(currentEntry.channelpartnerlist || {}) };
    if (!Array.isArray(currentChannelList[channelPartner])) {
      currentChannelList[channelPartner] = [];
    }
    currentChannelList[channelPartner].push(item);
    currentEntry.channelpartnerlist = currentChannelList;

    currentEntry[`${channelKey}_ty`] =
      (currentEntry[`${channelKey}_ty`] || 0) + cyRooms;
    currentEntry[`${channelKey}_ly`] =
      (currentEntry[`${channelKey}_ly`] || 0) + lyRooms;

    updated[targetIdx] = currentEntry;
  });

  updated.sort((a, b) => (a.stay_date > b.stay_date ? 1 : -1));
  return updated;
}

function formatEventsList(rawList) {
  if (!Array.isArray(rawList)) return [];
  const list = [];
  rawList.forEach((item) => {
    try {
      if (!item) return;
      let singleEventObj = {
        id: item?.eventid ? item?.eventid : (item?.id || 0),
        name: item?.eventname || item?.name || item?.eventName || item?.event || "",
        detail: item?.eventdetails || item?.detail || "",
        startDate: toCalendarYMD(item?.startdate || item?.startDate || item?.start_date),
        endDate: toCalendarYMD(item?.enddate || item?.endDate || item?.end_date || item?.startdate || item?.startDate),
        eventJson: item?.eventjson,
        eventResourceId: item?.eventsourceid,
      };
      if (item?.eventjson) {
        try {
          let decodedString = decodeURIComponent(item.eventjson);
          if (typeof decodedString === "string" && decodedString.startsWith('"') && decodedString.endsWith('"')) {
            decodedString = decodedString.slice(1, -1);
          }
          const eventJsonData = typeof decodedString === "object" ? decodedString : JSON.parse(decodedString);
          const desc = typeof eventJsonData?.description === "string"
            ? eventJsonData.description.replaceAll("+", " ")
            : (eventJsonData?.description || "");
          singleEventObj = {
            ...singleEventObj,
            labels: eventJsonData?.labels || "",
            rank: eventJsonData?.rank || 0,
            local_rank: eventJsonData?.local_rank || 0,
            phq_attendance: eventJsonData?.phq_attendance || 0,
            geo_lat: eventJsonData?.geo_lat || "",
            geo_long: eventJsonData?.geo_long || "",
            detail: desc || singleEventObj.detail,
          };
        } catch (jsonErr) {}
      }
      if (singleEventObj.name) {
        list.push(singleEventObj);
      }
    } catch (err) {}
  });
  return list;
}

export {
  CONFIG,
  AS_OF,
  PROPERTY,
  DAILY,
  RES17,
  DATES,
  PLANS,
  DEFAULT_PLANS_META,
  LEAD_BUCKETS,
  Q,
  LEVERS,
  RULES,
  RES_FIELDS,
  LB,
  ROOMS,
  LASTN,
  RES_COLS,
  CHANNELS,
  PROMO_OWNERS,
  PROMO_NAMES,
  PROMO_FENCES,
  PROMO_TYPE_LABEL,
  PROMO_RULES,
  enrichRatePlans,
  normalizeChannelName,
  extractChannelPairs,
  buildDatesFromDaily,
  bindDailyFromRecommendedRates,
  bindDailyFromRateShop,
  bindDailyFromEvents,
  bindDailyFromChannelStly,
  formatEventsList
};
